import moment from 'moment';
import { omit } from 'lodash-es';
import type {
  ICardMetricItem,
  ICardMetricsApiData,
  IYtdComputedFromService,
} from './components/TechlandYtdRevenueCard/cardMetricsTypes';
import type {
  ITechlandYtdRevenueCardProps,
  ITechlandYtdVarianceRow,
  TTechlandYtdComputedValueKey,
} from './components/TechlandYtdRevenueCard/types';
import {
  METRIC_YTD_BUDGET,
  METRIC_YTD_GROSS_REVENUE,
  METRIC_YTD_PLAN,
  METRIC_YTD_PROFIT,
  resolveStudioKpiTitleIconSrc,
  TECHLAND_GAME_CODE,
  TECHLAND_SCOPE_GROSS,
  TECHLAND_STUDIO_CODE,
} from './_constant';

export interface ITechlandCardMetricsRequestPayload {
  game_code: string;
  start_time?: string;
  end_time?: string;
  /** 与 HomeFilter granularityConfigs.type 一致：daily / weekly / monthly */
  period?: string;
  metrics: string[];
  trend_metrics: string[];
  filters: unknown;
  studio_code: string;
  logical_flag: string;
  is_confirm: boolean;
}

export interface ITechlandYtdFilterForFormat {
  start_time?: string;
  end_time?: string;
  scope?: string;
}

export interface ITechlandYtdMetricKeysForCompute {
  /** 与后端 card_list.metrics_name 完全一致（不做转换） */
  revenueMetricName: string;
  budgetMetricName: string;
  planMetricName: string;
}

interface ITechlandBeforeRequestData {
  filterVal?: {
    start_time?: string;
    end_time?: string;
    date_type?: string;
    scope?: string;
  };
  filterInitData?: unknown;
}

/** 组装 Techland card_metrics 请求体（与 HomeFilter + filterInitData 配合） */
export function buildTechlandCardMetricsRequest(
  data: ITechlandBeforeRequestData,
  metricsOverride?: string[],
): ITechlandCardMetricsRequestPayload {
  const fv = data?.filterVal;
  const scope = fv?.scope;
  const period = fv?.date_type ?? 'daily';
  const profitMetric = resolveProfitMetricName(scope);
  const metrics =
    metricsOverride && Array.isArray(metricsOverride) && metricsOverride.length > 0
      ? metricsOverride
      : [profitMetric, METRIC_YTD_BUDGET, METRIC_YTD_PLAN];
  return {
    game_code: TECHLAND_GAME_CODE,
    start_time: fv?.start_time,
    end_time: fv?.end_time,
    period,
    metrics,
    trend_metrics: [],
    filters: data?.filterInitData || {},
    studio_code: TECHLAND_STUDIO_CODE,
    logical_flag: "fallback_latest_date",
    is_confirm: false,
  };
}

export function techlandNum(v: unknown): number | '' {
  if (v === '' || v === null || v === undefined) {
    return '';
  }
  const n = Number(v);
  return Number.isNaN(n) ? '' : n;
}

/**
 * Budget / Plan 区指标（与主指标 YTD Revenue 同一数值口径）。
 * - Completion%：YTD Revenue ÷ YTD Budget（或 ÷ YTD Plan），存为比例（如 0.85），由 percent_int 展示为百分比。
 * - Vs.：YTD Revenue − YTD Budget（或 − YTD Plan）；正绿负红、负数带负号由 bmk00 + 行 tone 展示。
 * @param targetYtd YTD Budget 或 YTD Plan 的数值
 * @param ytdRevenue YTD Revenue（ytd_profit / ytd_gross_revenue 与主指标一致）
 */
export function techlandCalcYtdRevenueVsTarget(
  targetYtd: number | '' | string,
  ytdRevenue: number | '' | string,
): { completion: number | null; diff: number | null; } {
  if (targetYtd === '' || ytdRevenue === '') {
    return { completion: null, diff: null };
  }
  const t = typeof targetYtd === 'number' ? targetYtd : Number(targetYtd);
  const r = typeof ytdRevenue === 'number' ? ytdRevenue : Number(ytdRevenue);
  if (!Number.isFinite(r) || !Number.isFinite(t) || t === 0) {
    return { completion: null, diff: null };
  }
  return { completion: r / t, diff: r - t };
}

/**
 * 未在 pageConfig 中显式配置 revenue / ShowTime 指标名时的兜底（与 TECHLAND_SCOPE_* 对齐）。
 */
function resolveProfitMetricName(
  scope: string | undefined,
): typeof METRIC_YTD_PROFIT | typeof METRIC_YTD_GROSS_REVENUE {
  return scope === TECHLAND_SCOPE_GROSS
    ? METRIC_YTD_GROSS_REVENUE
    : METRIC_YTD_PROFIT;
}

/**
 * ShowTime 单指标 metrics_name 兜底：未配置 `showTimeMetricByScope` / `showTimeKeyByScope` 时使用。
 */
export function resolveTechlandShowTimeMetricKey(
  scope: string | undefined,
): 'profit' | 'gross_revenue' {
  return scope === TECHLAND_SCOPE_GROSS ? 'gross_revenue' : 'profit';
}

function rawMetricValue(
  list: ICardMetricItem[],
  name: string,
): string | null {
  const v = list.find(i => i?.metrics_name === name)?.value;
  if (v === '' || v === undefined || v === null) {
    return null;
  }
  return String(v);
}

/** 副标题结束日：ShowTime → 筛选 end_time → 利润 dod.this_time */
export function resolveTechlandYtdSubtitleEnd(
  filterVal: ITechlandYtdFilterForFormat | undefined,
  cardList: ICardMetricItem[],
  showTimeMap?: Record<string, string>,
  metricNameForTime?: string,
): string | undefined {
  const metricName = metricNameForTime ?? resolveProfitMetricName(filterVal?.scope);
  let showTimeLookupKey = metricName;
  if (metricName === METRIC_YTD_GROSS_REVENUE) {
    showTimeLookupKey = 'gross_revenue';
  } else if (metricName === METRIC_YTD_PROFIT) {
    showTimeLookupKey = 'profit';
  }
  let fromShow = showTimeMap?.[showTimeLookupKey];
  if (fromShow === undefined || fromShow === null || String(fromShow).trim() === '') {
    fromShow = showTimeMap?.[metricName];
  }
  if (fromShow !== undefined && fromShow !== null && String(fromShow).trim() !== '') {
    return String(fromShow).trim();
  }
  if (filterVal?.end_time) {
    return filterVal.end_time;
  }
  const item = cardList.find(i => i?.metrics_name === metricName);
  const raw = item?.dod?.this_time;
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return undefined;
  }
  const s = String(raw).trim();
  const d = moment(s);
  return d.isValid() ? d.format('YYYY-MM-DD') : s;
}

export type TTechlandYtdCardMetricsFormatted = ICardMetricsApiData & {
  _ytdSubtitleDate?: {
    subtitleDate?: string;
  };
  _ytdComputed?: IYtdComputedFromService;
  _cardProps?: ITechlandYtdRevenueCardProps;
};

function resolveVsToneForYtd(
  v: number | null | undefined,
): 'positive' | 'negative' | 'neutral' {
  if (v === null || v === undefined || v === 0) {
    return 'neutral';
  }
  return v > 0 ? 'positive' : 'negative';
}

/** pageConfig 未写 valueKey 时的默认：YTD 行用后端 metrics_name，其余为前端计算字段 */
const DEFAULT_BUDGET_ROW_KEYS: {
  ytd: TTechlandYtdComputedValueKey;
  completion: TTechlandYtdComputedValueKey;
  vsVariance: TTechlandYtdComputedValueKey;
} = {
  ytd: 'ytd_budget',
  completion: 'budgetCompletion',
  vsVariance: 'budgetVs',
};

const DEFAULT_PLAN_ROW_KEYS: {
  ytd: TTechlandYtdComputedValueKey;
  completion: TTechlandYtdComputedValueKey;
  vsVariance: TTechlandYtdComputedValueKey;
} = {
  ytd: 'ytd_plan',
  completion: 'planCompletion',
  vsVariance: 'planVs',
};

function pickComputedValue(
  computed: IYtdComputedFromService | undefined,
  key: TTechlandYtdComputedValueKey,
): string | number | null | undefined {
  if (!computed) {
    return undefined;
  }
  return computed[key] as string | number | null | undefined;
}

function mergeYtdMetricRow(
  row: ITechlandYtdRevenueCardProps['budgetGroup']['ytd'],
  fallbackKey: TTechlandYtdComputedValueKey,
  computed: IYtdComputedFromService | undefined,
): ITechlandYtdRevenueCardProps['budgetGroup']['ytd'] {
  const key = row.valueKey ?? fallbackKey;
  const raw = pickComputedValue(computed, key);
  const rest = omit(row, 'valueKey');
  return {
    ...rest,
    value: raw !== undefined && raw !== null ? raw : row.value,
  };
}

function mergeYtdVarianceRow(
  row: ITechlandYtdVarianceRow,
  fallbackKey: TTechlandYtdComputedValueKey,
  computed: IYtdComputedFromService | undefined,
): ITechlandYtdVarianceRow {
  const key = row.valueKey ?? fallbackKey;
  const raw = pickComputedValue(computed, key);
  const rest = omit(row, 'valueKey');
  const numVs =
    key === 'budgetVs' || key === 'planVs' ? (raw as number | null) : null;
  return {
    ...rest,
    value: raw !== undefined && raw !== null ? raw : row.value,
    tone:
      key === 'budgetVs' || key === 'planVs'
        ? resolveVsToneForYtd(numVs)
        : row.tone,
  };
}

/**
 * 将 pageConfig 中的卡片模板与 formatTechlandYtdCardMetricsData 结果合并为完整卡片 props。
 * 各行的 `valueKey` / `actualValueKey` 与 _ytdComputed 字段一致（后端 metrics_name 或前端计算字段名）；未配置时使用 DEFAULT_*。
 */
export function mergeTechlandYtdCardDisplayProps(
  formatted: TTechlandYtdCardMetricsFormatted,
  filterVal: ITechlandYtdFilterForFormat | undefined,
  base: ITechlandYtdRevenueCardProps,
): ITechlandYtdRevenueCardProps {
  const c = formatted._ytdComputed;
  const {
    actualValueKey,
    budgetGroup: cfgBudget,
    planGroup: cfgPlan,
    ...baseRest
  } = base;
  const resolvedActualKey: TTechlandYtdComputedValueKey =
    actualValueKey ??
    (filterVal?.scope === TECHLAND_SCOPE_GROSS
      ? 'ytd_gross_revenue'
      : 'ytd_profit');
  const actualRaw = pickComputedValue(c, resolvedActualKey);
  return {
    ...baseRest,
    subtitleDate:
      formatted._ytdSubtitleDate?.subtitleDate ??
      filterVal?.end_time ??
      base.subtitleDate,
    actualValue:
      actualRaw !== undefined && actualRaw !== null ? actualRaw : base.actualValue,
    budgetGroup: {
      hidden: cfgBudget.hidden,
      ytd: mergeYtdMetricRow(
        cfgBudget.ytd,
        DEFAULT_BUDGET_ROW_KEYS.ytd,
        c,
      ),
      completion: mergeYtdMetricRow(
        cfgBudget.completion,
        DEFAULT_BUDGET_ROW_KEYS.completion,
        c,
      ),
      vsVariance: mergeYtdVarianceRow(
        cfgBudget.vsVariance,
        DEFAULT_BUDGET_ROW_KEYS.vsVariance,
        c,
      ),
    },
    planGroup: {
      hidden: cfgPlan.hidden,
      ytd: mergeYtdMetricRow(cfgPlan.ytd, DEFAULT_PLAN_ROW_KEYS.ytd, c),
      completion: mergeYtdMetricRow(
        cfgPlan.completion,
        DEFAULT_PLAN_ROW_KEYS.completion,
        c,
      ),
      vsVariance: mergeYtdVarianceRow(
        cfgPlan.vsVariance,
        DEFAULT_PLAN_ROW_KEYS.vsVariance,
        c,
      ),
    },
  };
}

/**
 * card_metrics 响应加工：写入 _ytdComputed（仅数值）与副标题日期，不向 card_list 追加伪指标。
 *
 * YTD Revenue 与卡片主指标一致（scope 为 net 用 ytd_profit，gross 用 ytd_gross_revenue）。
 * - Budget Completion% = YTD Revenue ÷ YTD Budget；Vs. Budget = YTD Revenue − YTD Budget
 * - Plan Completion% = YTD Revenue ÷ YTD Plan；Vs. Plan = YTD Revenue − YTD Plan
 * - 主指标 ytd_* 为空串时：Completion% 按收入 0 与 Budget/Plan 计算（有分母则 0%，无分母仍 --）；Vs. 同为 0 − Budget/Plan。
 */
export function formatTechlandYtdCardMetricsData(
  res: ICardMetricsApiData | null | undefined,
  filterVal: ITechlandYtdFilterForFormat | undefined,
  showTimeMap?: Record<string, string>,
  metricKeys?: Partial<ITechlandYtdMetricKeysForCompute>,
): TTechlandYtdCardMetricsFormatted {
  const raw = res ?? { card_list: [], trend_list: [], breakdown: null };
  const list = [...(raw.card_list ?? [])];
  const revenueMetricName =
    metricKeys?.revenueMetricName ?? resolveProfitMetricName(filterVal?.scope);
  const budgetMetricName = metricKeys?.budgetMetricName ?? METRIC_YTD_BUDGET;
  const planMetricName = metricKeys?.planMetricName ?? METRIC_YTD_PLAN;

  const ytdRevenue = techlandNum(
    list.find(i => i?.metrics_name === revenueMetricName)?.value,
  );
  const ytdBudget = techlandNum(
    list.find(i => i?.metrics_name === budgetMetricName)?.value,
  );
  const ytdPlan = techlandNum(list.find(i => i?.metrics_name === planMetricName)?.value);
  const revenueForBudgetPct =
    ytdRevenue === '' && ytdBudget !== '' ? 0 : ytdRevenue;
  const revenueForPlanPct =
    ytdRevenue === '' && ytdPlan !== '' ? 0 : ytdRevenue;
  const budgetCalc = techlandCalcYtdRevenueVsTarget(ytdBudget, revenueForBudgetPct);
  const planCalc = techlandCalcYtdRevenueVsTarget(ytdPlan, revenueForPlanPct);
  const budgetVs = budgetCalc.diff;
  const planVs = planCalc.diff;
  const ytdComputed: IYtdComputedFromService = {
    ytd_budget: rawMetricValue(list, budgetMetricName),
    ytd_plan: rawMetricValue(list, planMetricName),
    budgetCompletion: budgetCalc.completion,
    budgetVs,
    planCompletion: planCalc.completion,
    planVs,
  };
  /**
   * 兼容 UI 侧默认 actualValueKey：仍写入 ytd_profit / ytd_gross_revenue 两个字段之一。
   * 若页面配置显式传 actualValueKey，则 merge 时会按配置取值，不依赖这里的默认字段名。
   */
  if (revenueMetricName === METRIC_YTD_PROFIT) {
    ytdComputed.ytd_profit = rawMetricValue(list, revenueMetricName);
  } else if (revenueMetricName === METRIC_YTD_GROSS_REVENUE) {
    ytdComputed.ytd_gross_revenue = rawMetricValue(list, revenueMetricName);
  }
  const endYmd = resolveTechlandYtdSubtitleEnd(
    filterVal,
    list,
    showTimeMap,
    revenueMetricName,
  );
  const base: TTechlandYtdCardMetricsFormatted = {
    ...raw,
    card_list: list,
    _ytdSubtitleDate: {
      subtitleDate: endYmd,
    },
    _ytdComputed: ytdComputed,
  };
  return base;
}

export function withCatalogFilter(
  filters: Record<string, unknown> | undefined,
  catalog: string,
): Record<string, unknown> {
  const base = { ...(filters || {}) };
  const custom = Array.isArray(base.custom_filters)
    ? (base.custom_filters as Array<{ key?: string; filter_list?: string[]; }>).map(
      item => ({ ...item }),
    )
    : [];
  const idx = custom.findIndex(item => item?.key === 'catalog');
  if (idx >= 0) {
    custom[idx] = { ...custom[idx], key: 'catalog', filter_list: [catalog] };
  } else {
    custom.push({ key: 'catalog', filter_list: [catalog] });
  }
  return {
    ...base,
    custom_filters: custom,
  };
}

function toNumberOrNull(v: unknown): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

export interface ITechlandByGameMergeRowConfig {
  nameEn: string;
  nameCn?: string;
  dataKey: string;
  titleIconSrcKey?: string;
}

export function buildTechlandByGameTableRow(
  rowCfg: ITechlandByGameMergeRowConfig,
  formatted: TTechlandYtdCardMetricsFormatted,
  isGross: boolean,
): Record<string, unknown> {
  const computed = formatted?._ytdComputed || {};
  return {
    name: rowCfg.nameEn,
    name_cn: rowCfg.nameCn || rowCfg.nameEn,
    icon: resolveStudioKpiTitleIconSrc({ titleIconSrcKey: rowCfg.titleIconSrcKey }) || '',
    ytd_profit: toNumberOrNull(
      isGross ? computed.ytd_gross_revenue : computed.ytd_profit,
    ),
    ytd_budget: toNumberOrNull(computed.ytd_budget),
    ytd_plan: toNumberOrNull(computed.ytd_plan),
    budgetCompletion: computed.budgetCompletion ?? null,
    planCompletion: computed.planCompletion ?? null,
  };
}

export function mergeTechlandByGameTableData(
  data: Record<string, unknown> | undefined,
  rows: ITechlandByGameMergeRowConfig[],
  filterVal?: { scope?: string; },
): {
    data: Record<string, unknown>[];
    loading: boolean;
    errorMsg?: string;
    errorType?: string;
  } {
  if (!rows?.length) {
    return { data: [], loading: false };
  }
  const isGross = filterVal?.scope === TECHLAND_SCOPE_GROSS;
  let loading = false;
  let errorMsg: string | undefined;
  let errorType: string | undefined;
  const result: Record<string, unknown>[] = [];

  rows.forEach(rowCfg => {
    const envelope = data?.[rowCfg.dataKey] as
      | {
        loading?: boolean;
        data?: TTechlandYtdCardMetricsFormatted;
        errorMsg?: string;
        errorType?: string;
      }
      | undefined;

    if (!envelope) {
      loading = true;
      return;
    }
    if (envelope.loading) {
      loading = true;
      return;
    }
    if (envelope.errorMsg) {
      errorMsg = envelope.errorMsg;
      errorType = envelope.errorType || 'apiError';
      return;
    }
    if (!envelope.data) {
      errorMsg = errorMsg || 'Data unavailable';
      errorType = errorType || 'apiError';
      return;
    }
    result.push(buildTechlandByGameTableRow(rowCfg, envelope.data, isGross));
  });

  if (errorMsg) {
    return {
      data: [],
      loading: false,
      errorMsg,
      errorType,
    };
  }

  return {
    data: result,
    loading: loading || result.length < rows.length,
  };
}
